import { travelerFacingLocator } from '@voll-intelligence/domain';

import type { ChatUiTripVoucher } from '@voll-intelligence/genui-contract';
import { useGenUiI18n, type GenUiKey, type GenUiLocale } from '../ui/i18n.js';
import { fmtDateTime, fmtPlaceAt } from './trip_format.js';
import { KvGrid, type KvRow } from './trip_primitives.js';

/**
 * Detalhe operacional por modal de um voucher (FLIGHT / HOTEL / BUS / RENTAL /
 * MOBILITY) renderizado como KvGrid (`.tkv`). Campos nulos viram estado vazio
 * rotulado, nunca "null".
 */

type Details = NonNullable<ChatUiTripVoucher['details']>;
// Date fields on `details` are ISO strings (the string-dated
// `ChatUiVoucherDetailsSchema`), so they flow straight into `fmtDateTime`.
type FlightSeg = Extract<Details, { kind: 'FLIGHT' }>['segments'][number];
type BusSeg = Extract<Details, { kind: 'BUS' }>['segments'][number];

type Str = string | null | undefined;
type Num = number | null | undefined;

type Row = KvRow;
type Translate = (key: GenUiKey, values?: Record<string, string | number>) => string;

/** Localizador de segmento (FLIGHT/BUS) que apenas repete a Reserva do voucher
 *  vira ruído — o bloco meta já a mostra. Suprime quando idêntico. */
function segLocator(locator: Str, reservationNumber: Str): Str {
  return locator && locator === reservationNumber ? null : locator;
}

function Block({ label, rows }: { readonly label: string; readonly rows: Row[] }) {
  return (
    <div className="vacc__block">
      <span className="vacc__blabel">{label}</span>
      <KvGrid rows={rows} />
    </div>
  );
}

/** Rótulo "Ida/Volta · trecho N de M" a partir do tipo de perna + posição. */
function segmentLabel(type: Str, index: number, total: number, t: Translate): string {
  let dir: string;
  if (type === 'OUTWARD') {
    dir = t('voucher.segment.outbound');
  } else if (type === 'BACK') {
    dir = t('voucher.segment.return');
  } else {
    dir = t('voucher.segment.generic');
  }
  return total > 1 ? t('voucher.segment.count', { dir, index: index + 1, total }) : dir;
}

/** Rótulo de conexões do trecho: sem dado (null), "Direto", ou "N paradas". */
function connectionsLabel(stops: Num, t: Translate): string | null {
  if (stops == null) return null;
  if (stops === 0) return t('common.direct');
  return t(stops === 1 ? 'voucher.detail.stop_one' : 'voucher.detail.stop_other', {
    count: stops,
  });
}

function flightRows(
  seg: FlightSeg,
  reservationNumber: Str,
  t: Translate,
  locale: GenUiLocale,
): Row[] {
  const company = [seg.airline, seg.flightNumbers.length ? seg.flightNumbers.join('/') : null]
    .filter(Boolean)
    .join(' · ');
  // O código exibido é o DA COMPANHIA (reservas NDC trazem um interno longo em
  // `locator` que o balcão rejeita — issue #2090), e só se for código: status do
  // fornecedor ("RESERVADO") não é (#6368); só depois vem a supressão de eco.
  const locator = segLocator(travelerFacingLocator(seg), reservationNumber);
  return [
    { k: t('voucher.detail.airline'), v: company || null },
    ...(locator ? [{ k: t('voucher.detail.locator'), v: locator } satisfies Row] : []),
    {
      k: t('voucher.detail.departure'),
      v: fmtPlaceAt(seg.departureAirport, fmtDateTime(seg.departLocal, seg.departAt, locale)),
    },
    {
      k: t('voucher.detail.arrival'),
      v: fmtPlaceAt(seg.arrivalAirport, fmtDateTime(seg.arriveLocal, seg.arriveAt, locale)),
    },
    { k: t('voucher.detail.duration'), v: seg.duration ?? null },
    {
      k: t('voucher.detail.connections'),
      v: connectionsLabel(seg.stops, t),
    },
    { k: t('voucher.detail.cabin'), v: seg.cabinClass ?? null },
    { k: t('voucher.detail.seat'), v: seg.seat ?? null, empty: t('common.toDefine') },
    { k: t('voucher.detail.baggage'), v: seg.baggage ?? null },
  ];
}

function busRows(seg: BusSeg, reservationNumber: Str, t: Translate, locale: GenUiLocale): Row[] {
  const locator = segLocator(seg.locator, reservationNumber);
  return [
    { k: t('voucher.detail.operator'), v: seg.company ?? null },
    ...(locator ? [{ k: t('voucher.detail.locator'), v: locator } satisfies Row] : []),
    { k: t('voucher.detail.boarding'), v: seg.departureStation ?? seg.departureAddress ?? null },
    { k: t('voucher.detail.disembark'), v: seg.arrivalStation ?? seg.arrivalAddress ?? null },
    { k: t('voucher.detail.departure'), v: fmtDateTime(null, seg.departAt, locale) },
    { k: t('voucher.detail.arrival'), v: fmtDateTime(null, seg.arriveAt, locale) },
    { k: t('voucher.detail.class'), v: seg.seatClass ?? null },
    { k: t('voucher.detail.seat'), v: seg.seat ?? null, empty: t('common.toDefine') },
  ];
}

function agencyAt(
  agency: { name: Str; address: Str; city: Str } | null,
  at: string | null,
): string | null {
  const place = agency?.name ?? agency?.city ?? agency?.address ?? null;
  return fmtPlaceAt(place, at);
}

/** `String(n)` ou null — evita ternários repetidos nas grades. */
function num(n: Num): string | null {
  return n == null ? null : String(n);
}

function hotelRows(
  h: Extract<Details, { kind: 'HOTEL' }>,
  t: Translate,
  locale: GenUiLocale,
): Row[] {
  const cp = h.cancellationPolicy;
  let policy: string | null = null;
  if (cp != null) {
    policy =
      cp.value == null
        ? t('voucher.detail.available')
        : t('voucher.detail.fine', { value: cp.value });
  }
  return [
    { k: t('voucher.detail.hotel'), v: h.name ?? null },
    { k: t('voucher.detail.address'), v: h.address ?? null },
    { k: t('voucher.detail.city'), v: h.city ?? null },
    {
      k: t('voucher.detail.checkIn'),
      v: fmtPlaceAt(fmtDateTime(null, h.checkIn, locale), h.checkInHour ?? null),
    },
    {
      k: t('voucher.detail.checkOut'),
      v: fmtPlaceAt(fmtDateTime(null, h.checkOut, locale), h.checkOutHour ?? null),
    },
    { k: t('voucher.detail.nights'), v: num(h.nights) },
    { k: t('voucher.detail.rooms'), v: num(h.rooms) },
    { k: t('voucher.detail.guests'), v: num(h.guests) },
    { k: t('voucher.detail.cancellationPolicy'), v: policy, empty: t('common.toDefine') },
  ];
}

function rentalRows(r: Extract<Details, { kind: 'RENTAL' }>, t: Translate): Row[] {
  return [
    { k: t('voucher.detail.rentalCompany'), v: r.pickupAgency?.name ?? null },
    { k: t('voucher.detail.pickup'), v: agencyAt(r.pickupAgency, null) },
    { k: t('voucher.detail.dropoff'), v: agencyAt(r.dropoffAgency, null) },
    { k: t('voucher.detail.model'), v: r.carModel ?? null },
    { k: t('voucher.detail.group'), v: r.carGroup ?? null },
  ];
}

function mobilityRows(m: Extract<Details, { kind: 'MOBILITY' }>, t: Translate): Row[] {
  return [
    { k: t('voucher.detail.category'), v: m.category ?? null },
    { k: t('voucher.detail.distance'), v: m.distance == null ? null : `${String(m.distance)} km` },
    { k: t('voucher.detail.duration'), v: m.duration ?? null },
  ];
}

export function VoucherDetail({
  details,
  reservationNumber,
}: {
  readonly details: Details | null;
  readonly reservationNumber?: string | null;
}) {
  const { t, locale } = useGenUiI18n();
  if (!details) return null;

  switch (details.kind) {
    case 'FLIGHT': {
      const n = details.segments.length;
      return (
        <>
          {details.segments.map((seg, i) => (
            <Block
              key={`seg-${String(i)}`}
              label={segmentLabel(seg.type, i, n, t)}
              rows={flightRows(seg, reservationNumber, t, locale)}
            />
          ))}
        </>
      );
    }
    case 'BUS': {
      const n = details.segments.length;
      return (
        <>
          {details.segments.map((seg, i) => (
            <Block
              key={`bus-${String(i)}`}
              label={segmentLabel(seg.type, i, n, t)}
              rows={busRows(seg, reservationNumber, t, locale)}
            />
          ))}
        </>
      );
    }
    case 'HOTEL':
      return <Block label={t('voucher.block.lodging')} rows={hotelRows(details, t, locale)} />;
    case 'RENTAL':
      return <Block label={t('voucher.block.rental')} rows={rentalRows(details, t)} />;
    case 'MOBILITY':
      return <Block label={t('voucher.block.mobility')} rows={mobilityRows(details, t)} />;
    default:
      return null;
  }
}
